import React, { useEffect, useState } from 'react';

import NearMeIcon from '@mui/icons-material/NearMe';
import { styled } from '@mui/material/styles';
import {
    Box,
    Button,
    Grid,
    IconButton,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import { Map, useMap, useApiIsLoaded } from '@vis.gl/react-google-maps';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import { useGeolocated } from 'react-geolocated';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    ReactHookFormInput,
    ReactHookFormIntlNumberInput,
} from 'common/components/form/bootstrap';
import {
    ItemGeocodeResult,
    ListGeocodeResult,
    WrapperGeocodeResult,
} from 'common/components/maps';
import {
    DEFAULT_LAT,
    DEFAULT_LNG,
    DEFAULT_MAP_PREVIEW_ZOOM,
} from 'common/constants';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import { FETCH_GEOCODE_SUCCESS } from '../actionTypes';
import * as selectors from '../selectors';
import validate from '../validate';

const DEBOUNCE_TIME = 1000;

const MapWrapper = styled(Box)`
    height: 300px;
    position: relative;
    & div:nth-of-type(-n + 1) {
        -webkit-border-radius: 10px;
        -moz-border-radius: 10px;
        border-radius: 10px;
    }
`;

const Form = ({
    addressPlaceholder,
    center = { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
    fetchGeocodePoint,
    geocodePointData,
    geocodeResult,
    handleClickCancel,
    handleGeocode,
    initialValues,
    isFetchingGeocodePoint,
    isForLayout,
    isSaving,
    onSubmit,
    setEmptyGeocodeResult,
    setShowGeocodeResults,
    showGeocodeResults,
    zoom = DEFAULT_MAP_PREVIEW_ZOOM,
}) => {
    const { t } = useTranslation();
    const { coords, isGeolocationEnabled } = useGeolocated({
        positionOptions: { enableHighAccuracy: false },
        userDecisionTimeout: 5000,
    });
    const [marker, setMarker] = useState(null);
    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    const position = watch('position');
    const positionLatitude = position?.latitude;
    const positionLongitude = position?.longitude;

    const [google, setGoogle] = useState(null);
    const apiIsLoaded = useApiIsLoaded();
    const map = useMap();

    const onDrag = (e) => {
        const latlng = new google.maps.LatLng(
            e.map.center.lat(),
            e.map.center.lng(),
        );
        marker.setPosition(latlng);
    };
    const onDragend = (e) => {
        if (handleOnPositionChanged) handleOnPositionChanged(null, e?.map);
    };

    const onZoomChanged = (e) => {
        if (handleOnPositionChanged) handleOnPositionChanged(null, e?.map);
    };

    useEffect(() => {
        if (!apiIsLoaded) return;
        setGoogle(window.google);
    }, [apiIsLoaded]);

    useEffect(() => {
        reset(initialValues);
        if (map && google && position) {
            map.setTilt(0);
            var latlng = new google.maps.LatLng(
                positionLatitude ? positionLatitude : center.lat,
                positionLongitude ? positionLongitude : center.lng,
            );

            const tempMarker = new google.maps.marker.AdvancedMarkerElement({
                map: map,
                position: latlng,
            });

            tempMarker.addListener('dragend', (evt) => {
                setValue('position', {
                    latitude: evt.latLng.lat(),
                    longitude: evt.latLng.lng(),
                });

                fetchGeocodePoint(evt.latLng.lat(), evt.latLng.lng());
            });

            setMarker(tempMarker);
            map.setCenter(latlng);

            return () => {
                if (tempMarker) {
                    tempMarker.map = null;
                }
            };
        }
    }, [map, google]);

    useEffect(() => {
        if (!isNil(geocodePointData)) setValue('title', geocodePointData);
    }, [geocodePointData]);

    useEffect(() => {
        if (!isEmpty(position) && marker !== null)
            if (
                (positionLatitude !== null &&
                    positionLatitude !== marker.getPosition().lat()) ||
                (positionLongitude !== null &&
                    positionLongitude !== marker.getPosition().lng())
            ) {
                var latlng = new google.maps.LatLng(
                    positionLatitude,
                    positionLongitude,
                );

                marker.setPosition(latlng);
                map.setCenter(latlng);
            }
    }, [positionLatitude, positionLongitude, marker]);

    const [timer, setTimer] = useState(null);
    const handleOnChange = (e) => {
        setValue('title', e);
        clearTimeout(timer);
        setTimer(setTimeout(() => triggerChange(e), DEBOUNCE_TIME));
    };

    const triggerChange = (value) => {
        if (value.length < 6) {
            setEmptyGeocodeResult();
            return;
        }
        handleGeocode(value);
    };

    const handleOnPositionChanged = (_, map) => {
        setValue('position', {
            latitude: map.center.lat(),
            longitude: map.center.lng(),
        });
        fetchGeocodePoint(map.center.lat(), map.center.lng());
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
                <Grid size={{ md: isForLayout ? 9 : 18, xs: 18 }}>
                    <Grid container>
                        <Grid size={18}>
                            {isForLayout && (
                                <Typography variant="caption">
                                    {t('Address')}
                                </Typography>
                            )}
                            <Box>
                                {geocodeResult &&
                                    geocodeResult.length > 0 &&
                                    showGeocodeResults && (
                                        <WrapperGeocodeResult>
                                            <ListGeocodeResult
                                                style={
                                                    isForLayout
                                                        ? {
                                                              transform:
                                                                  'translate3d(0px, 45px, 0px)',
                                                          }
                                                        : {
                                                              transform:
                                                                  'translate3d(0px, 38px, 0px)',
                                                          }
                                                }
                                            >
                                                {geocodeResult.map(
                                                    (result, i) => (
                                                        <ItemGeocodeResult
                                                            key={`geocode-result-${i}`}
                                                            onClick={() => {
                                                                setValue(
                                                                    'title',
                                                                    result.formatted_address,
                                                                );

                                                                const {
                                                                    lat,
                                                                    lng,
                                                                } =
                                                                    result
                                                                        .geometry
                                                                        .location;

                                                                setValue(
                                                                    'position',
                                                                    {
                                                                        latitude:
                                                                            lat,
                                                                        longitude:
                                                                            lng,
                                                                    },
                                                                );
                                                            }}
                                                        >
                                                            {
                                                                result.formatted_address
                                                            }
                                                        </ItemGeocodeResult>
                                                    ),
                                                )}
                                            </ListGeocodeResult>
                                        </WrapperGeocodeResult>
                                    )}
                                <ReactHookFormInput
                                    autoComplete="off"
                                    control={control}
                                    disabled={
                                        isSaving || isFetchingGeocodePoint
                                    }
                                    label={
                                        !isForLayout
                                            ? t('Address').concat('*')
                                            : null
                                    }
                                    name="title"
                                    onChange={(e) =>
                                        handleOnChange(e.target.value)
                                    }
                                    onBlur={() => {
                                        setTimeout(() => {
                                            setShowGeocodeResults(false);
                                        }, 150);
                                    }}
                                    onFocus={() => setShowGeocodeResults(true)}
                                    placeholder={
                                        addressPlaceholder
                                            ? addressPlaceholder
                                            : null
                                    }
                                    variant={
                                        isForLayout ? 'filled' : 'outlined'
                                    }
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid size="grow">
                            {isForLayout && (
                                <Typography variant="caption">
                                    {t('Latitude')}
                                </Typography>
                            )}
                            <ReactHookFormIntlNumberInput
                                control={control}
                                disabled={isSaving}
                                fullWidth
                                label={t('Latitude')}
                                name="position.latitude"
                                variant={isForLayout ? 'filled' : 'outlined'}
                            />
                        </Grid>

                        <Grid size="grow">
                            {isForLayout && (
                                <Typography variant="caption">
                                    {t('Longitude')}
                                </Typography>
                            )}
                            <Box display="flex" gap={2} sx={{ width: '100%' }}>
                                <ReactHookFormIntlNumberInput
                                    control={control}
                                    disabled={isSaving}
                                    fullWidth
                                    label={t('Longitude')}
                                    name="position.longitude"
                                    variant={
                                        isForLayout ? 'filled' : 'outlined'
                                    }
                                />
                                <Box mb={2}>
                                    <IconButton
                                        disabled={
                                            !isGeolocationEnabled || isSaving
                                        }
                                        onClick={() => {
                                            if (!isNil(coords)) {
                                                setValue('position', {
                                                    latitude: coords.latitude,
                                                    longitude: coords.longitude,
                                                });

                                                fetchGeocodePoint(
                                                    coords.latitude,
                                                    coords.longitude,
                                                );
                                            }
                                        }}
                                    >
                                        <NearMeIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box>
                        <Typography variant="caption">
                            *{t('Required fields')}
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ md: isForLayout ? 9 : 18, xs: 18 }}>
                    <MapWrapper>
                        <Map
                            fullscreenControl={true}
                            defaultCenter={{
                                lat: +center.lat,
                                lng: +center.lng,
                            }}
                            mapId={import.meta.env.VITE_GOOGLE_MAPS_DEFAULT_ID}
                            mapTypeControl={true}
                            mapTypeId="roadmap"
                            onDrag={onDrag}
                            onDragend={onDragend}
                            onZoomChanged={onZoomChanged}
                            rotateControl={false}
                            scaleControl={true}
                            streetViewControl={false}
                            defaultZoom={zoom}
                        />
                    </MapWrapper>
                </Grid>
            </Grid>
            <Grid container mt={2}>
                <Grid
                    sx={{
                        display: 'flex',
                        flexDirection: { md: 'row', xs: 'column' },
                        justifyContent: { md: 'right', xs: 'center' },
                    }}
                >
                    <Button
                        onClick={handleClickCancel}
                        sx={{
                            mr: { md: 2, xs: 0 },
                            order: { md: 1, xs: 2 },
                            width: { md: 'auto', xs: '100%' },
                        }}
                        variant="text"
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        sx={{
                            mb: { md: 0, xs: 2 },
                            order: { md: 2, xs: 1 },
                            width: { md: 'auto', xs: '100%' },
                        }}
                        type="submit"
                        variant="outlined"
                    >
                        {t('Save')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

Form.propTypes = {
    addressPlaceholder: PropTypes.string,
    center: PropTypes.object,
    fetchGeocodePoint: PropTypes.func,
    geocodePointData: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    geocodeResult: PropTypes.array,
    handleClickCancel: PropTypes.func,
    handleGeocode: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingGeocodePoint: PropTypes.bool,
    isForLayout: PropTypes.bool,
    isSaving: PropTypes.bool,
    onSubmit: PropTypes.func,
    setEmptyGeocodeResult: PropTypes.func,
    setShowGeocodeResults: PropTypes.func,
    showGeocodeResults: PropTypes.bool,
    zoom: PropTypes.number,
};

const mapStateToProps = createStructuredSelector({
    geocodePointData: selectors.getGeocodePointData,
    isFetchingGeocodePoint: selectors.getIsFetchingGeocodePoint,
    showGeocodeResults: selectors.showGeocodeResults,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
    fetchGeocodePoint: (lat, lng) =>
        dispatch(actions.fetchGeocodePoint(lat, lng, ownProps.name)),
    setEmptyGeocodeResult: () =>
        dispatch({
            type: FETCH_GEOCODE_SUCCESS,
            payload: [],
            name: ownProps.name,
        }),
    setShowGeocodeResults: (value) =>
        dispatch(actions.setShowGeocodeResults(value, ownProps.name)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Form);
